import {
  COOKIE_SESSION_ID,
  COOKIE_SESSION_ID_LEGACY,
  COOKIE_SESSION_NEW,
  COOKIE_TRANSACTION_ID,
  COOKIE_VISITOR_ID,
  STORAGE_KEY,
  VISITOR_ID_MAX_AGE,
} from './constants';
import type { StoredSession } from './types';

// ---------------------------------------------------------------------------
// Cookie helpers
// ---------------------------------------------------------------------------

/**
 * Read a cookie by name. Never throws — an unreadable jar reads as an empty one.
 *
 * Two things in here raise on a hostile page. `document.cookie` itself is a
 * `SecurityError` in a sandboxed frame without `allow-same-origin` and on an
 * opaque origin; `decodeURIComponent` is a `URIError` on a value carrying a
 * malformed escape. Either used to unwind out of `SessionManager`'s
 * constructor, and this read is the first thing `#resolveSession()` performs —
 * so the shared context vanished before a single id had been resolved.
 *
 * Reporting absence is the right reading of an unreachable jar: callers already
 * handle "no cookie" by minting instead of resuming, which is exactly the
 * outcome a browser that will not hand over its cookies should produce.
 */
export function getCookie(name: string): string | null {
  try {
    for (const cookie of document.cookie.split('; ')) {
      const [cookieName, cookieValue] = cookie.split('=');
      if (cookieName === name) return decodeURIComponent(cookieValue);
    }
  } catch {
    // Jar unreachable, or the stored value is not decodable — treat as absent.
  }
  return null;
}

/**
 * Write a cookie. Never throws.
 *
 * Every cookie this package writes — `mf_sid`, `mf_vid`, `transaction_id` —
 * funnels through here, so the guard belongs at this one point rather than at
 * each call site, where the next writer added would forget it.
 *
 * Swallowing rather than rethrowing is deliberate. The caller has already
 * resolved or minted the value it is asking us to persist and goes on using it,
 * so the render stays internally consistent even though the next page view will
 * mint again. Rethrowing trades a non-durable id for no id at all: the
 * assignment below raises in a sandboxed frame, and the exception unwound past
 * `#resolveVisitorId()`'s `return`, out of the constructor, and past the
 * `window.PS.session` assignment in `index.ts` — losing a perfectly good
 * visitor id and the whole shared context with it. Mirrors `persist()` in
 * `event-listener-client/src/visitor-id.ts`.
 */
export function setCookie(name: string, value: string, maxAgeSec: number, domain?: string, secure = false): void {
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax; max-age=${maxAgeSec}`;
  if (domain) cookie += `; domain=${domain}`;
  if (secure) cookie += '; Secure';
  try {
    document.cookie = cookie;
  } catch {
    // Storage refused (sandboxed frame, opaque origin, extension). The value is
    // already in the caller's hands; only its durability is lost.
  }
}

export function deleteCookie(name: string, domain?: string): void {
  setCookie(name, '', 0, domain);
}

// ---------------------------------------------------------------------------
// Session ID cookie
// ---------------------------------------------------------------------------

export function getSessionIdFromCookie(): string | null {
  return getCookie(COOKIE_SESSION_ID);
}

/**
 * Always host-only + Secure (on https), mirroring the edge-minted cookie.
 * A Domain attribute would create a second mf_sid alongside the edge's
 * host-only one, making reads ambiguous.
 */
export function setSessionIdCookie(id: string, maxAgeSec: number): void {
  setCookie(COOKIE_SESSION_ID, id, maxAgeSec, undefined, window.location.protocol === 'https:');
}

export function getLegacySessionIdFromCookie(): string | null {
  return getCookie(COOKIE_SESSION_ID_LEGACY);
}

export function deleteLegacySessionIdCookie(): void {
  // Legacy cookie was always written host-only — delete must match
  deleteCookie(COOKIE_SESSION_ID_LEGACY);
}

/** Edge-set fresh-mint marker (mf_sid_new). Never written or deleted from JS. */
export function getSessionNewMarkerFromCookie(): string | null {
  return getCookie(COOKIE_SESSION_NEW);
}

// ---------------------------------------------------------------------------
// Transaction ID cookie
// ---------------------------------------------------------------------------

export function getTransactionIdFromCookie(): string | null {
  return getCookie(COOKIE_TRANSACTION_ID);
}

export function setTransactionIdCookie(value: string, maxAgeSec: number, domain?: string): void {
  setCookie(COOKIE_TRANSACTION_ID, value, maxAgeSec, domain);
}

// ---------------------------------------------------------------------------
// Visitor ID cookie (mf_vid) — per ADR-0015
// Normally server-set by CloudFront Function (first-party, 1yr TTL).
// JS fallback for non-owned domains (Safari ITP caps to 7 days).
// ---------------------------------------------------------------------------

export function getVisitorIdFromCookie(): string | null {
  return getCookie(COOKIE_VISITOR_ID);
}

export function setVisitorIdFallbackCookie(id: string, domain?: string): void {
  setCookie(COOKIE_VISITOR_ID, id, VISITOR_ID_MAX_AGE, domain);
}

// ---------------------------------------------------------------------------
// URL parameter helpers
// ---------------------------------------------------------------------------

export function getURLParameter(name: string): string | undefined {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) ?? undefined;
}

// ---------------------------------------------------------------------------
// localStorage — rich session state
// ---------------------------------------------------------------------------

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredSession;
  } catch {
    return null;
  }
}

export function saveSession(session: StoredSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // localStorage full or unavailable — degrade silently
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
